import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Shield, Eye, MapPin, ArrowRight, CheckCircle, FileText, Car } from 'lucide-react';

const HERO_BG = "/hero-bg.png";

const benefits = [
  {
    icon: Clock,
    title: 'Rapid',
    description: 'Procesăm dosarul dumneavoastră în cel mai scurt timp posibil.'
  },
  {
    icon: Shield,
    title: 'Legal',
    description: 'Toate serviciile respectă legislația în vigoare din România.'
  },
  {
    icon: Eye,
    title: 'Transparent',
    description: 'Prețuri clare, fără costuri ascunse. Știți exact ce plătiți.'
  },
  {
    icon: MapPin,
    title: 'Fără drumuri inutile',
    description: 'Noi ne ocupăm de deplasări. Economisiți timp prețios.'
  }
];

const popularServices = [
  {
    id: 'srv_contract',
    name: 'Contract vânzare-cumpărare',
    price: 20,
    description: 'Întocmire contract auto'
  },
  {
    id: 'srv_radiere_full',
    name: 'Radiere completă',
    price: 60,
    description: 'Taxe locale + poliție'
  },
  {
    id: 'srv_platinum',
    name: 'Pachet Platinum',
    price: 140,
    description: 'Serviciu complet VIP'
  }
];

const Home = () => {
  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src={HERO_BG} 
            alt="Contract signing" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-blue-600/20 text-blue-300 text-sm font-medium rounded-full mb-6 animate-fade-in">
              Servicii administrative auto
            </span>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in stagger-1" data-testid="hero-title">
              Noi rezolvăm actele.
              <br />
              <span className="text-blue-400">Tu câștigi timp.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 animate-fade-in stagger-2" data-testid="hero-subtitle">
              Servicii complete pentru acte auto și documente oficiale, fără stres. 
              Profesionalism, rapiditate și legalitate garantată.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
              <Link to="/comanda" data-testid="hero-order-btn">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30 active:scale-95 transition-all w-full sm:w-auto">
                  Comandă serviciu
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/servicii" data-testid="hero-services-btn">
                <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 w-full sm:w-auto">
                  Vezi serviciile
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-4" data-testid="benefits-title">
              De ce AnDeni Time Solutions?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Oferim servicii de încredere pentru toate nevoile dumneavoastră legate de actele auto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card 
                key={benefit.title} 
                className="bg-white p-8 rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300"
                data-testid={`benefit-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-lg bg-blue-50 flex items-center justify-center mb-6">
                    <benefit.icon className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Services Preview */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-4" data-testid="services-preview-title">
                Servicii populare
              </h2>
              <p className="text-lg text-slate-600">
                Cele mai solicitate servicii de către clienții noștri.
              </p>
            </div>
            <Link to="/servicii" data-testid="view-all-services-link">
              <Button variant="outline" className="border-slate-200 hover:bg-white">
                Vezi toate serviciile
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {popularServices.map((service, index) => (
              <Card 
                key={service.id}
                className="bg-white p-8 rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`popular-service-${index}`}
              >
                <CardContent className="p-0">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      {index === 0 ? <FileText className="h-5 w-5 text-blue-600" /> : 
                       index === 1 ? <Car className="h-5 w-5 text-blue-600" /> :
                       <CheckCircle className="h-5 w-5 text-blue-600" />}
                    </div>
                    <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {service.price} EUR
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {service.description}
                  </p>
                  <Link to={`/comanda?service=${service.id}`} data-testid={`select-service-${index}-btn`}>
                    <Button variant="outline" className="w-full border-slate-200 hover:bg-slate-50 hover:border-blue-200 group-hover:border-blue-300 transition-all">
                      Selectează
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white mb-6" data-testid="cta-title">
            Gata să economisești timp?
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
            Începe acum și lasă-ne să ne ocupăm de actele tale. 
            Procesul este simplu, rapid și sigur.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/comanda" data-testid="cta-order-btn">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30 active:scale-95 transition-all">
                Plasează o comandă
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="cta-contact-btn">
              <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10">
                Contactează-ne
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
